import { useEffect, useState, useRef, useCallback } from "react";
import { Loader2, CheckCircle2, IdCard, FileText, Eye, AlertCircle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const ACCEPT = "application/pdf,.pdf";

// La ranura del contrato firmado pertenece al CRÉDITO (su solicitud), no al expediente del
// cliente: se liga con `referencia_id` y el backend resuelve el `credito_id` desde ahí.
const esRanuraContrato = (d) => (d.key || d.tipo) === "contrato_firmado"
  || (d.tipo || "") === "Contrato firmado";

// Carga de documentos de identidad ligados al EXPEDIENTE DEL CLIENTE (cliente_id).
// - Reusa POST /documentos/upload (categoria=expediente, cifrado) → aparecen en Perfil 360.
// - Lee GET /documentos/checklist/{cliente_id} SOLO para saber (a) qué tipo de persona es
//   el cliente y filtrar ranuras que no apliquen (ej. CURP no aplica a PM).
// - Lee GET /documentos?cliente_id=... para hidratar el estado "ya cargado" de CADA
//   ranura, con nombre_archivo + fecha_carga + doc_id + enlace ver. Aplica igual a docs
//   canónicos y no canónicos (unificado feb-2026).
// - jul-2026 (fix causa raíz): AUTO-SUBIDA al seleccionar el archivo. Antes había que pulsar
//   un botón-ícono aparte por ranura; si el operador solo elegía el archivo y pulsaba
//   "Guardar" en la solicitud, NADA se subía (pérdida silenciosa). Ahora elegir = subir.
// - feb-2026 (unificación): un solo badge "Ya cargado (fecha) · nombre · ver + Reemplazar"
//   para TODAS las ranuras (canónicas y no canónicas). El input desnudo desaparece
//   cuando hay un doc Vigente; se puede reemplazar con botón dedicado que dispara el
//   `reemplaza_doc_id` del backend (previo → Reemplazado, nuevo → Vigente, 1 solo activo).
//   Sin borrados: la trazabilidad queda intacta.
export function DocsIdentidadCliente({ clienteId, docs = [], referenciaId = "" }) {
  const [busy, setBusy] = useState(null);
  const [subidos, setSubidos] = useState({});
  const [errores, setErrores] = useState({});
  const [checklist, setChecklist] = useState(null);
  const [existentes, setExistentes] = useState([]);  // documentos previos del cliente
  const inputsRef = useRef({});                       // input file oculto por ranura

  const cargarChecklist = useCallback(async () => {
    if (!clienteId) { setChecklist(null); setExistentes([]); return; }
    try {
      const { data } = await api.get(`/documentos/checklist/${clienteId}`);
      setChecklist(data);
    } catch { setChecklist(null); }
    // Documentos existentes del cliente para hidratar cada ranura (canónica o no).
    try {
      const { data } = await api.get(`/documentos`, { params: { cliente_id: clienteId } });
      setExistentes(Array.isArray(data) ? data : []);
    } catch { setExistentes([]); }
  }, [clienteId]);
  useEffect(() => { cargarChecklist(); }, [cargarChecklist]);

  // Set de claves canónicas del checklist KYC del cliente. Sirve SOLO para ocultar
  // ranuras que no aplican a este tipo_persona (PM no lleva CURP, etc.).
  const canonicosDelChecklist = new Set(
    (checklist?.items || []).map((i) => i.canonical));

  // Devuelve el documento persistido que corresponde a la ranura `d`, unificando
  // canónicos y no canónicos. Para canónicos usa `tipo_canonico` (backfill del backend
  // garantiza que todos los docs lo tienen); para no canónicos matchea por
  // `tipo_documento` textual. Devuelve el más reciente por fecha_carga.
  const resolverDocPresente = (d) => {
    const cats = new Set(["expediente", "credito", "override"]);
    const canon = d.canonical || null;
    const claves = [d.tipo, d.key].filter(Boolean);
    const matches = existentes.filter((e) => {
      if (e.estatus && e.estatus !== "Vigente") return false;
      if (!cats.has((e.categoria || "").toLowerCase())) return false;
      // sep-2026 · EL CONTRATO ES DOCUMENTO DEL CRÉDITO, NO DEL CLIENTE. Esta ranura solo
      // reconoce —y por tanto solo puede reemplazar— el contrato de ESTA solicitud. Antes
      // tomaba cualquier contrato del cliente, así que al capturar el segundo crédito el
      // contrato del primero quedaba «Reemplazado». El resto de las ranuras (INE, CURP,
      // comprobantes) no cambia en nada.
      if (esRanuraContrato(d)) {
        if (!(e.tipo_canonico === "contrato" || (e.tipo_documento || "") === "contrato_firmado")) return false;
        return referenciaId
          ? e.referencia_id === referenciaId
          : (!e.referencia_id && !e.credito_id);
      }
      if (canon) {
        // El backend guarda `tipo_canonico` en cada doc (backfill idempotente);
        // reconoce alias como INE anverso/reverso ~ identificacion sin repetir el mapa.
        return e.tipo_canonico === canon;
      }
      return claves.some((k) => (e.tipo_documento || "") === k);
    });
    if (!matches.length) return null;
    return matches.reduce((a, b) =>
      (String(a.fecha_carga || a.created_at || "") > String(b.fecha_carga || b.created_at || "")) ? a : b);
  };

  const abrirDocPrevio = async (docId) => {
    try {
      const { data } = await api.post(`/documentos/${docId}/signed-url`);
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const url = data.url.startsWith("http") ? data.url : `${backend}${data.url}`;
      window.open(url, "_blank", "noopener");
    } catch (e) { toast.error(apiError(e)); }
  };

  const debeMostrar = (d) => {
    // Ítems sin canonical (INE, contrato_firmado) siempre.
    if (!d.canonical) return true;
    // Ítem canónico: solo si el checklist del cliente lo contempla (PF vs PM).
    // Si aún no cargó el checklist, mostramos por default (evita ocultar antes de tiempo).
    if (!checklist) return true;
    return canonicosDelChecklist.has(d.canonical);
  };

  // AUTO-SUBIDA: se dispara al seleccionar el archivo (sin botón intermedio). Si ya hay
  // un doc previo Vigente para esta ranura, el backend lo marca como "Reemplazado" al
  // recibir `reemplaza_doc_id`. Sin borrados: trazabilidad completa (jul-2026).
  const subir = async (d, file, docPrevio) => {
    if (!file) return;
    if (!clienteId) {
      toast.error("La solicitud aún no tiene un cliente asignado. Selecciona primero al cliente y vuelve a elegir el archivo.");
      setErrores((p) => ({ ...p, [d.tipo]: "Sin cliente asignado: el archivo NO se subió." }));
      return;
    }
    setBusy(d.tipo);
    setErrores((p) => ({ ...p, [d.tipo]: null }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo_documento", d.key || d.tipo);
      fd.append("categoria", "expediente");
      fd.append("clasificacion", "Soporte");
      fd.append("cliente_id", clienteId);
      // El contrato viaja ligado a su trámite (solicitud → crédito). Las demás ranuras
      // siguen subiendo exactamente igual que antes.
      if (esRanuraContrato(d) && referenciaId) fd.append("referencia_id", referenciaId);
      if (docPrevio && docPrevio.id) fd.append("reemplaza_doc_id", docPrevio.id);
      await api.post("/documentos/upload", fd);
      setSubidos((p) => ({ ...p, [d.tipo]: true }));
      toast.success(docPrevio
        ? `${d.tipo} reemplazado en el expediente (el anterior queda marcado como reemplazado).`
        : `${d.tipo} cargado y cifrado en el expediente del cliente.`);
      cargarChecklist();
    } catch (e) {
      const msg = apiError(e);
      setErrores((p) => ({ ...p, [d.tipo]: msg }));
      toast.error(`No se pudo subir ${d.tipo}: ${msg}`);
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-2" data-testid="docs-identidad-cliente">
      {docs.filter(debeMostrar).map((d) => {
        const previo = resolverDocPresente(d);
        const yaCargado = !!previo;
        const subiendo = busy === d.tipo;
        return (
          <div key={d.tipo} className="rounded-md border border-slate-200 p-3" data-testid={`docid-${d.tipo}`}>
            <Label className="mb-1 flex flex-wrap items-center gap-1.5 text-xs font-medium text-slate-700">
              {d.obligatorio ? <IdCard className="h-3.5 w-3.5 text-primary" /> : <FileText className="h-3.5 w-3.5 text-slate-400" />}
              {d.tipo}{" "}
              {d.obligatorio
                ? <span className="text-amber-600">(recomendado)</span>
                : <span className="text-slate-400">(opcional)</span>}
              {subiendo && (
                <span className="ml-1 inline-flex items-center gap-1 text-slate-500" data-testid={`docid-subiendo-${d.tipo}`}>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Subiendo…
                </span>
              )}
            </Label>
            {d.hint && !yaCargado && <p className="mb-1.5 text-[11px] text-slate-500">{d.hint}</p>}
            {errores[d.tipo] && (
              <p className="mb-1.5 flex items-center gap-1 text-[11px] font-medium text-red-600"
                 data-testid={`docid-error-${d.tipo}`}>
                <AlertCircle className="h-3 w-3 shrink-0" />
                {errores[d.tipo]} Vuelve a seleccionar el archivo para reintentar.
              </p>
            )}
            {/* Input file SIEMPRE presente en el DOM. Oculto cuando ya hay doc cargado
                (se dispara desde el botón "Reemplazar"). */}
            <input
              ref={(el) => { inputsRef.current[d.tipo] = el; }}
              type="file"
              accept={ACCEPT}
              data-testid={`docid-input-${d.tipo}`}
              disabled={subiendo}
              className={yaCargado && !subiendo
                ? "hidden"
                : "block h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) subir(d, f, previo);
              }} />
            {yaCargado && !subiendo && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-[11px] text-emerald-800"
                   data-testid={`docid-cargado-${d.tipo}`}>
                <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                  <CheckCircle2 className="h-3 w-3 shrink-0" />
                  <span className="font-medium">Ya cargado{previo.fecha_carga ? ` (${previo.fecha_carga})` : ""}</span>
                  <span className="inline-flex items-center gap-1 text-emerald-700">
                    <FileText className="h-3 w-3" /> {previo.nombre_archivo || "archivo"}{previo.tamano ? ` · ${previo.tamano} bytes` : ""}
                  </span>
                  <Button type="button" size="sm" variant="ghost"
                    className="h-5 gap-1 px-1.5 text-[11px] text-emerald-700 hover:text-emerald-800"
                    data-testid={`docid-ver-${d.tipo}`}
                    onClick={() => abrirDocPrevio(previo.id)}>
                    <Eye className="h-3 w-3" /> ver
                  </Button>
                  <Button type="button" size="sm" variant="outline"
                    className="h-6 gap-1 px-2 text-[11px]"
                    data-testid={`docid-reemplazar-${d.tipo}`}
                    onClick={() => inputsRef.current[d.tipo]?.click()}>
                    <RotateCcw className="h-3 w-3" /> Reemplazar documento
                  </Button>
                </div>
              </div>
            )}
            {!yaCargado && (
              <p className="mt-1 text-[10px] text-slate-400">
                El archivo se sube y cifra automáticamente al seleccionarlo (solo PDF, máx. 15 MB).
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
